// ============================================================================
// PROMPT 24 — YAMOOPAY CHIEF GLOBAL EXPANSION OFFICER COMMAND CENTER
// Unified Executive Globalization, Localization, Country Packs & Market Factory OS
// ============================================================================

import React, { useState } from 'react';
import {
  Globe,
  MapPin,
  Languages,
  Layers,
  Sparkles,
  FileCheck2,
  BookOpen,
  DollarSign,
  TrendingUp,
  Search,
  AlertTriangle,
  CheckCircle2,
  Scale,
  Shield,
  ArrowRight,
  Calculator,
  RefreshCw,
  Zap,
  Sliders,
  ExternalLink,
  ChevronRight,
  Terminal,
  Activity
} from 'lucide-react';
import {
  LANGUAGES_REGISTRY,
  SCRIPTS_REGISTRY,
  LOCALES_REGISTRY,
  MARKETS_REGISTRY,
  COUNTRY_PACKS_REGISTRY,
  FINANCIAL_CONVENTIONS,
  FINANCIAL_TERMINOLOGY_REGISTRY,
  DO_NOT_TRANSLATE_REGISTRY,
  SAMPLE_TRANSLATION_MESSAGES,
  SAMPLE_TRANSLATION_UNITS,
  TRANSLATION_MEMORY_STORE,
  LOCALIZED_SEARCH_INTENTS,
  MARKET_LAUNCH_CANDIDATES,
  SAMPLE_LOCALIZATION_INCIDENTS
} from '../../services/globalization/globalizationRegistry';
import {
  YamooPayNumberFormatter,
  YamooPayCurrencyFormatter,
  YamooPayDateFormatter,
  LocalizedFinancialInputParser,
  PseudolocalizationEngine,
  BidirectionalUIEngine,
  LocaleFallbackEngine,
  HreflangGraphService,
  MarketLaunchFactoryService
} from '../../services/globalization/localizationEngines';
import { globalUserPreferences } from '../../services/globalization/userPreferenceContext';
import { AppView } from '../../types';

interface GlobalExpansionCommandCenterProps {
  onNavigateToView?: (view: AppView) => void;
}

export const GlobalExpansionCommandCenter: React.FC<GlobalExpansionCommandCenterProps> = ({ onNavigateToView }) => {
  const [activeTab, setActiveTab] = useState<
    | 'overview'
    | 'markets'
    | 'launch-factory'
    | 'country-packs'
    | 'languages-locales'
    | 'tms-translations'
    | 'terminology'
    | 'input-parser'
    | 'rtl-studio'
    | 'international-seo'
    | 'incidents'
  >('overview');

  // Parser sandbox state
  const [testInput, setTestInput] = useState('1 250,75');
  const [testLocale, setTestLocale] = useState('fr-FR');
  const parsedResult = LocalizedFinancialInputParser.parse(testInput, testLocale);

  // Formatter sandbox state
  const [formatterAmount, setFormatterAmount] = useState(125450.5);
  const [formatterCurrency, setFormatterCurrency] = useState('EUR');
  const [formatterLocale, setFormatterLocale] = useState('fr-FR');

  // Pseudoloc sandbox state
  const [pseudoText, setPseudoText] = useState('Calculate your exact paycheck take-home pay with 2026 tax brackets.');

  // Market Launch Gate candidate selector
  const [selectedCandidateId, setSelectedCandidateId] = useState('LC-DE');
  const currentCandidate = MARKET_LAUNCH_CANDIDATES.find((c) => c.id === selectedCandidateId) || MARKET_LAUNCH_CANDIDATES[0];
  const gateEvaluation = MarketLaunchFactoryService.evaluateGates(currentCandidate);

  // Hreflang tool path
  const [hreflangPath, setHreflangPath] = useState('/salary-engine');
  const hreflangTags = HreflangGraphService.generateHreflangTags(hreflangPath, ['en-US', 'en-GB', 'fr-FR', 'fr-CA', 'ar-SA', 'de-DE']);
  const hreflangValidation = HreflangGraphService.validateReciprocalLinks(hreflangTags);

  // Filter states
  const [selectedMarketCode, setSelectedMarketCode] = useState<string>('US');
  const currentMarket = MARKETS_REGISTRY.find((m) => m.marketCode === selectedMarketCode) || MARKETS_REGISTRY[0];

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 pb-24">
      {/* 1. Header Banner */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3">
                <div className="p-2.5 bg-blue-600 text-white rounded-2xl shadow-sm">
                  <Globe className="w-6 h-6" />
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <h1 className="text-2xl font-black text-slate-900">Chief Global Expansion Officer Command Center</h1>
                    <span className="text-xs font-bold px-2.5 py-0.5 rounded-full bg-blue-100 text-blue-800 font-mono">
                      P24 ENGINE
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5">
                    One Global YamooPay Core • Versioned Market Packs • BCP 47 & CLDR • RTL & Bidi Safety • Strict Jurisdiction Protection
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => globalUserPreferences.togglePseudolocalization()}
                className="px-3.5 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white hover:bg-slate-50 flex items-center gap-2 transition-colors cursor-pointer"
              >
                <Zap className="w-3.5 h-3.5 text-amber-500" />
                <span>Toggle Pseudoloc (QA)</span>
              </button>
              {onNavigateToView && (
                <button
                  onClick={() => onNavigateToView('admin-super-command-center')}
                  className="px-3.5 py-2 text-xs font-bold rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors cursor-pointer"
                >
                  Super Admin
                </button>
              )}
            </div>
          </div>

          {/* 2. Top Executive KPI Bar */}
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mt-6">
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Active Markets</div>
              <div className="text-lg font-black text-slate-900 mt-0.5">8 Markets</div>
              <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">4 Paid • 4 Free</div>
            </div>
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Active Locales</div>
              <div className="text-lg font-black text-slate-900 mt-0.5">12 BCP 47</div>
              <div className="text-[10px] text-blue-600 font-semibold mt-0.5">100% CLDR synced</div>
            </div>
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Languages</div>
              <div className="text-lg font-black text-slate-900 mt-0.5">8 Langs</div>
              <div className="text-[10px] text-slate-500 font-semibold mt-0.5">LTR + RTL Native</div>
            </div>
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Launch Pipeline</div>
              <div className="text-lg font-black text-blue-600 mt-0.5">3 In-Flight</div>
              <div className="text-[10px] text-slate-500 font-semibold mt-0.5">DE, SA, JP</div>
            </div>
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">TMS Approval</div>
              <div className="text-lg font-black text-emerald-600 mt-0.5">96.4%</div>
              <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">Zero auto-pub leaks</div>
            </div>
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Organic Traffic</div>
              <div className="text-lg font-black text-slate-900 mt-0.5">2.4M/mo</div>
              <div className="text-[10px] text-emerald-600 font-semibold mt-0.5">+28% YoY Int&apos;l</div>
            </div>
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Hreflang Health</div>
              <div className="text-lg font-black text-emerald-600 mt-0.5">100%</div>
              <div className="text-[10px] text-slate-500 font-semibold mt-0.5">Reciprocal verified</div>
            </div>
            <div className="bg-slate-50 border border-slate-200/80 rounded-xl p-3">
              <div className="text-[10px] font-bold text-slate-500 uppercase">Defect Rate</div>
              <div className="text-lg font-black text-emerald-600 mt-0.5">0.02%</div>
              <div className="text-[10px] text-slate-500 font-semibold mt-0.5">Strict Isolation</div>
            </div>
          </div>

          {/* 3. Navigation Tabs */}
          <div className="flex items-center gap-1 overflow-x-auto pt-5 border-t border-slate-200/80 mt-5 no-scrollbar">
            {[
              { id: 'overview', label: 'Executive Overview', icon: Globe },
              { id: 'markets', label: 'Markets & Coverage', icon: MapPin },
              { id: 'launch-factory', label: 'Launch Factory', icon: Sparkles },
              { id: 'country-packs', label: 'Country & Financial Packs', icon: Layers },
              { id: 'languages-locales', label: 'Languages & Locales', icon: Languages },
              { id: 'tms-translations', label: 'TMS & Memory', icon: BookOpen },
              { id: 'terminology', label: 'Financial Terminology', icon: FileCheck2 },
              { id: 'input-parser', label: 'Number Parser Sandbox', icon: Calculator },
              { id: 'rtl-studio', label: 'RTL & Bidi Studio', icon: Sliders },
              { id: 'international-seo', label: 'Hreflang & SEO', icon: Search },
              { id: 'incidents', label: 'Incidents & Kill Switches', icon: AlertTriangle }
            ].map((tab) => {
              const Icon = tab.icon;
              const isSelected = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as any)}
                  className={`px-3.5 py-2 rounded-xl text-xs font-bold flex items-center gap-2 whitespace-nowrap transition-all cursor-pointer ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-xs'
                      : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* 4. Tab Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* ========================================================================= */}
        {/* TAB 1: EXECUTIVE OVERVIEW */}
        {/* ========================================================================= */}
        {activeTab === 'overview' && (
          <div className="space-y-6">
            {/* Core Principle Callout Banner */}
            <div className="bg-linear-to-r from-blue-900 via-indigo-900 to-slate-900 text-white rounded-2xl p-6 shadow-md">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="space-y-2">
                  <span className="text-[10px] font-black tracking-widest uppercase bg-blue-500/30 text-blue-200 px-3 py-1 rounded-full border border-blue-400/20">
                    The 8 Golden Invariants of YamooPay Globalization
                  </span>
                  <h3 className="text-xl font-black">Dimensional Independence Architecture</h3>
                  <p className="text-xs text-blue-100/80 max-w-3xl leading-relaxed">
                    Language ≠ Country • Country ≠ Market • Market ≠ Jurisdiction • Jurisdiction ≠ Currency • Currency ≠ Locale • Locale ≠ Tax Residence • Translation ≠ Localization • Display Format ≠ Financial Precision.
                  </p>
                </div>
                <div className="bg-white/10 backdrop-blur-md rounded-xl p-4 border border-white/10 text-xs space-y-1.5 shrink-0">
                  <div className="flex items-center gap-2 text-emerald-300 font-bold">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>One Global Core Backend</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-300 font-bold">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Zero Cross-Border Rule Fallback</span>
                  </div>
                  <div className="flex items-center gap-2 text-emerald-300 font-bold">
                    <CheckCircle2 className="w-4 h-4" />
                    <span>No Applet Forking per Country</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Launch Waves Grid */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h3 className="text-base font-bold text-slate-900">Global Market Rollout Waves</h3>
                  <p className="text-xs text-slate-500">Staged expansion progression with strict financial and legal gates.</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
                {[
                  { wave: 'Wave 0', title: 'Global Core', status: 'COMPLETED', markets: 'English Global, Framework, BCP47, TMS' },
                  { wave: 'Wave 1', title: 'North America', status: 'ACTIVE', markets: 'United States, Canada (en-US, en-CA, fr-CA)' },
                  { wave: 'Wave 2', title: 'Europe', status: 'ACTIVE', markets: 'UK, France, Germany (en-GB, fr-FR, de-DE)' },
                  { wave: 'Wave 3', title: 'Asia-Pacific', status: 'BUILDING', markets: 'Japan, Australia, Singapore (ja-JP)' },
                  { wave: 'Wave 4', title: 'ME & LATAM', status: 'LIMITED', markets: 'Saudi Arabia, Brazil, Spain (ar-SA, pt-BR)' },
                  { wave: 'Wave 5', title: 'Africa', status: 'ACTIVE', markets: 'Cameroon, Nigeria (fr-CM, en-NG, XAF, NGN)' }
                ].map((w, idx) => (
                  <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-black text-slate-500 uppercase">{w.wave}</span>
                        <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full ${
                          w.status === 'COMPLETED' || w.status === 'ACTIVE'
                            ? 'bg-emerald-100 text-emerald-800'
                            : w.status === 'LIMITED'
                            ? 'bg-amber-100 text-amber-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {w.status}
                        </span>
                      </div>
                      <div className="font-bold text-sm text-slate-900 mt-1">{w.title}</div>
                      <p className="text-xs text-slate-500 mt-1 leading-snug">{w.markets}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Quick Market Snapshot Table */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
              <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between bg-slate-50">
                <h3 className="font-bold text-slate-900 text-sm">Active Operating Markets Snapshot</h3>
                <button
                  onClick={() => setActiveTab('markets')}
                  className="text-xs text-blue-600 font-bold hover:underline flex items-center gap-1 cursor-pointer"
                >
                  <span>View All & Coverage Matrix</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100/75 text-slate-600 font-bold uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3">Market</th>
                      <th className="px-6 py-3">Wave</th>
                      <th className="px-6 py-3">Status</th>
                      <th className="px-6 py-3">Supported Locales</th>
                      <th className="px-6 py-3">Default Currency</th>
                      <th className="px-6 py-3">Statutory Engines</th>
                      <th className="px-6 py-3">Paid Ready</th>
                      <th className="px-6 py-3">Score</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                    {MARKETS_REGISTRY.slice(0, 6).map((mkt) => (
                      <tr key={mkt.marketCode} className="hover:bg-slate-50/80 transition-colors">
                        <td className="px-6 py-3.5 font-bold text-slate-900 flex items-center gap-2">
                          <span>{mkt.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 rounded font-mono text-slate-700">{mkt.marketCode}</span>
                        </td>
                        <td className="px-6 py-3 text-slate-500 font-mono text-[11px]">{mkt.launchWave.replace('WAVE_', 'W')}</td>
                        <td className="px-6 py-3">
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${
                            mkt.status === 'ACTIVE_PAID'
                              ? 'bg-emerald-100 text-emerald-800'
                              : mkt.status === 'ACTIVE_FREE'
                              ? 'bg-blue-100 text-blue-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {mkt.status}
                          </span>
                        </td>
                        <td className="px-6 py-3">
                          <div className="flex gap-1 flex-wrap">
                            {mkt.supportedLocales.map((loc) => (
                              <span key={loc} className="px-1.5 py-0.5 bg-slate-100 rounded text-[10px] font-mono text-slate-600">
                                {loc}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-6 py-3 font-mono font-bold text-slate-900">{mkt.defaultCurrency}</td>
                        <td className="px-6 py-3 text-slate-500">{mkt.productCoverage.filter((p) => p.coverage === 'FULLY_LOCALIZED').length} Full Engines</td>
                        <td className="px-6 py-3">
                          {mkt.paidMonetizationEnabled ? (
                            <span className="text-emerald-600 font-bold flex items-center gap-1">
                              <CheckCircle2 className="w-3.5 h-3.5" /> Yes (Stripe)
                            </span>
                          ) : (
                            <span className="text-slate-400 font-medium">Free Mode</span>
                          )}
                        </td>
                        <td className="px-6 py-3 font-bold text-blue-600">{mkt.opportunityScore}/100</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 2: MARKETS & PRODUCT COVERAGE MATRIX */}
        {/* ========================================================================= */}
        {activeTab === 'markets' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white p-6 rounded-2xl border border-slate-200">
              <div>
                <h3 className="text-base font-bold text-slate-900">Operating Markets & Coverage Matrix</h3>
                <p className="text-xs text-slate-500">Inspect statutory engine support, currency defaults, and compliance state per territory.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-bold">Select Market:</span>
                <select
                  value={selectedMarketCode}
                  onChange={(e) => setSelectedMarketCode(e.target.value)}
                  className="px-3 py-1.5 rounded-xl border border-slate-300 text-xs font-bold bg-white text-slate-800"
                >
                  {MARKETS_REGISTRY.map((m) => (
                    <option key={m.marketCode} value={m.marketCode}>
                      {m.name} ({m.marketCode})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Selected Market Detail Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6 border-b border-slate-200">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-2xl bg-blue-100 text-blue-700 flex items-center justify-center font-black text-lg">
                    {currentMarket.countryCode}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-lg font-black text-slate-900">{currentMarket.name}</h4>
                      <span className={`px-2.5 py-0.5 rounded-full text-xs font-bold ${
                        currentMarket.status === 'ACTIVE_PAID'
                          ? 'bg-emerald-100 text-emerald-800'
                          : currentMarket.status === 'ACTIVE_FREE'
                          ? 'bg-blue-100 text-blue-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}>
                        {currentMarket.status}
                      </span>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Region: <strong>{currentMarket.region}</strong> • Pack Version: <strong>{currentMarket.countryPackVersion}</strong> • Owner: {currentMarket.owner}
                    </p>
                  </div>
                </div>

                <div className="flex gap-3 text-xs">
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-center min-w-[100px]">
                    <div className="text-[10px] text-slate-500 uppercase font-bold">Currency</div>
                    <div className="font-black text-slate-900 mt-0.5">{currentMarket.defaultCurrency}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-center min-w-[100px]">
                    <div className="text-[10px] text-slate-500 uppercase font-bold">Jurisdiction</div>
                    <div className="font-black text-slate-900 mt-0.5">{currentMarket.jurisdictionPackId}</div>
                  </div>
                  <div className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-center min-w-[100px]">
                    <div className="text-[10px] text-slate-500 uppercase font-bold">Opportunity</div>
                    <div className="font-black text-blue-600 mt-0.5">{currentMarket.opportunityScore}/100</div>
                  </div>
                </div>
              </div>

              {/* Product Coverage for this market */}
              <div>
                <h5 className="text-xs font-black uppercase tracking-wider text-slate-400 mb-3">Product Coverage per Tool</h5>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {currentMarket.productCoverage.map((tool) => (
                    <div key={tool.toolId} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 flex flex-col justify-between">
                      <div>
                        <div className="flex items-center justify-between">
                          <span className="text-[10px] font-mono text-slate-500">{tool.category}</span>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${
                            tool.coverage === 'FULLY_LOCALIZED'
                              ? 'bg-emerald-100 text-emerald-800'
                              : tool.coverage === 'LOCALIZED'
                              ? 'bg-blue-100 text-blue-800'
                              : tool.coverage === 'GLOBAL_GENERIC'
                              ? 'bg-slate-200 text-slate-700'
                              : 'bg-amber-100 text-amber-800'
                          }`}>
                            {tool.coverage}
                          </span>
                        </div>
                        <div className="font-bold text-sm text-slate-900 mt-1.5">{tool.toolName}</div>
                        {tool.ruleSetVersion && (
                          <div className="text-[11px] text-slate-500 mt-1">
                            Ruleset: <strong className="text-slate-700">{tool.ruleSetVersion}</strong>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 3: MARKET LAUNCH FACTORY */}
        {/* ========================================================================= */}
        {activeTab === 'launch-factory' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 shadow-xs">
              <div>
                <h3 className="text-base font-bold text-slate-900">Repeatable Market Launch Pipeline</h3>
                <p className="text-xs text-slate-500">Transform new country expansion from custom engineering into an auditable 12-stage factory pipeline.</p>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 font-bold">Candidate:</span>
                <select
                  value={selectedCandidateId}
                  onChange={(e) => setSelectedCandidateId(e.target.value)}
                  className="px-3 py-1.5 rounded-xl border border-slate-300 text-xs font-bold bg-white text-slate-800"
                >
                  {MARKET_LAUNCH_CANDIDATES.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.marketCode} Candidate (Score: {c.opportunityScore})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Candidate Pipeline Progress Card */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-6">
              <div className="flex items-center justify-between border-b border-slate-200 pb-4">
                <div>
                  <h4 className="text-base font-black text-slate-900">
                    Candidate: {currentCandidate.marketCode} ({currentCandidate.targetLaunchWave})
                  </h4>
                  <p className="text-xs text-slate-500">Current Stage: <strong className="text-blue-600">{currentCandidate.currentStage}</strong></p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500 font-bold">Target Free Launch:</div>
                  <div className="text-sm font-black text-slate-900">{currentCandidate.targetFreeLaunchDate}</div>
                </div>
              </div>

              {/* 12-Stage Visual Breadcrumb */}
              <div className="overflow-x-auto pb-2">
                <div className="flex items-center gap-1.5 min-w-[750px]">
                  {[
                    'OPPORTUNITY',
                    'DISCOVERY',
                    'BUSINESS_CASE',
                    'COUNTRY_PACK',
                    'LOCALIZATION',
                    'FINANCIAL_RULES',
                    'COMPLIANCE',
                    'PAYMENT',
                    'SEO',
                    'QUALITY_CERT',
                    'LIMITED_ROLLOUT',
                    'ACTIVE_FREE'
                  ].map((stage, idx) => {
                    const isCurrent = currentCandidate.currentStage === stage;
                    const isPast = idx < 10; // illustrative
                    return (
                      <div
                        key={stage}
                        className={`px-2.5 py-1.5 rounded-lg text-[10px] font-bold text-center flex-1 ${
                          isCurrent
                            ? 'bg-blue-600 text-white shadow-xs'
                            : isPast
                            ? 'bg-slate-100 text-slate-700'
                            : 'bg-slate-50 text-slate-400 border border-slate-200'
                        }`}
                      >
                        {stage.replace('_', ' ')}
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Gate Evaluation Checklist */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4 border-t border-slate-200">
                <div className="space-y-3">
                  <h5 className="text-xs font-black uppercase tracking-wider text-slate-500">Stage Gate Checklist</h5>
                  <div className="space-y-2 text-xs">
                    {[
                      { key: 'countryPackValid', label: 'Country Pack Schema & Metadata Validated', passed: currentCandidate.gatesPassed.countryPackValid },
                      { key: 'localeUxPassed', label: 'Locale & Bidirectional RTL Visual QA Passed', passed: currentCandidate.gatesPassed.localeUxPassed },
                      { key: 'financialDataPassed', label: 'Financial Rules Independent Provenance Verified', passed: currentCandidate.gatesPassed.financialDataPassed },
                      { key: 'compliancePassed', label: 'Regional Compliance & Privacy Gate (Prompt 22)', passed: currentCandidate.gatesPassed.compliancePassed },
                      { key: 'accessibilityPassed', label: 'WCAG 2.2 AA & European Accessibility Act (EAA)', passed: currentCandidate.gatesPassed.accessibilityPassed },
                      { key: 'qualityCertified', label: 'P23 Release Quality Certificate Issued', passed: currentCandidate.gatesPassed.qualityCertified },
                      { key: 'paymentReadyIfPaid', label: 'Payment Gateway & Local Settlement Tax Verified', passed: currentCandidate.gatesPassed.paymentReadyIfPaid }
                    ].map((g) => (
                      <div key={g.key} className="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 border border-slate-200">
                        <span className="font-medium text-slate-700">{g.label}</span>
                        {g.passed ? (
                          <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded-md flex items-center gap-1 text-[10px]">
                            <CheckCircle2 className="w-3 h-3" /> PASSED
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 bg-amber-100 text-amber-800 font-bold rounded-md flex items-center gap-1 text-[10px]">
                            <AlertTriangle className="w-3 h-3" /> PENDING
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

                {/* Multidimensional Opportunity Score Breakdown */}
                <div className="space-y-3">
                  <h5 className="text-xs font-black uppercase tracking-wider text-slate-500">Opportunity Scorecard Dimensions</h5>
                  <div className="space-y-2.5">
                    {currentCandidate.dimensions.map((dim, idx) => (
                      <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200">
                        <div className="flex items-center justify-between text-xs font-bold">
                          <span className="text-slate-800">{dim.name} (wt: {dim.weight * 100}%)</span>
                          <span className="text-blue-600">{dim.score}/100</span>
                        </div>
                        <div className="w-full bg-slate-200 h-1.5 rounded-full mt-1.5 overflow-hidden">
                          <div className="bg-blue-600 h-full rounded-full" style={{ width: `${dim.score}%` }} />
                        </div>
                        <p className="text-[11px] text-slate-500 mt-1">{dim.rationale}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Launch Outcome Banner */}
              <div className={`p-4 rounded-xl border flex items-center justify-between ${
                gateEvaluation.canLaunchFree
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-900'
                  : 'bg-amber-50 border-amber-200 text-amber-900'
              }`}>
                <div>
                  <div className="text-xs font-bold uppercase">Launch Certification Outcome</div>
                  <div className="font-black text-sm mt-0.5">
                    {gateEvaluation.canLaunchFree ? 'APPROVED FOR ACTIVE_FREE LAUNCH' : 'LAUNCH GATES PENDING / BLOCKED'}
                  </div>
                  {gateEvaluation.missingFreeGates.length > 0 && (
                    <div className="text-xs mt-1">
                      Missing prerequisites: {gateEvaluation.missingFreeGates.join(', ')}
                    </div>
                  )}
                </div>
                <button
                  disabled={!gateEvaluation.canLaunchFree}
                  className={`px-4 py-2 rounded-xl text-xs font-bold transition-colors ${
                    gateEvaluation.canLaunchFree
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white cursor-pointer'
                      : 'bg-slate-300 text-slate-500 cursor-not-allowed'
                  }`}
                >
                  Promote to Active Free
                </button>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 4: COUNTRY & FINANCIAL CONVENTION PACKS */}
        {/* ========================================================================= */}
        {activeTab === 'country-packs' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">Versioned Country Packs & Financial Conventions</h3>
              <p className="text-xs text-slate-500">Declarative, composable configuration packs without application forking.</p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Country Packs Catalog */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
                <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Country Packs Registry</h4>
                <div className="space-y-3">
                  {COUNTRY_PACKS_REGISTRY.map((cp) => (
                    <div key={cp.countryCode} className="p-4 rounded-xl border border-slate-200 bg-slate-50 hover:bg-slate-100/50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <span className="font-black text-slate-900 text-sm">{cp.name}</span>
                          <span className="text-[10px] px-1.5 py-0.5 bg-slate-200 font-mono rounded">{cp.countryCode}</span>
                          <span className="text-[10px] font-mono text-blue-600 font-bold">v{cp.version}</span>
                        </div>
                        <span className="text-xs font-mono font-bold text-slate-700">{cp.defaultCurrency}</span>
                      </div>
                      <div className="text-xs text-slate-500 mt-2">
                        Subdivisions: {cp.subdivisions.map((s) => s.name).join(', ')}
                      </div>
                      <div className="flex flex-wrap gap-1.5 mt-2.5">
                        {cp.activeRuleEngines.map((eng) => (
                          <span key={eng} className="text-[10px] px-2 py-0.5 rounded bg-blue-50 text-blue-700 font-mono font-semibold">
                            {eng}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Financial Convention Packs */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
                <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Financial Convention Packs</h4>
                <div className="space-y-3">
                  {FINANCIAL_CONVENTIONS.map((fc) => (
                    <div key={fc.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50">
                      <div className="flex items-center justify-between">
                        <div className="font-bold text-sm text-slate-900">{fc.name}</div>
                        <span className="text-[10px] px-2 py-0.5 bg-indigo-100 text-indigo-800 font-bold rounded font-mono">
                          {fc.id}
                        </span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600 mt-3">
                        <div>Interest standard: <strong>{fc.interestConvention}</strong></div>
                        <div>Tax Year Start: <strong>{fc.taxYearStart}</strong></div>
                        <div>Fuel Unit: <strong>{fc.units.fuel}</strong></div>
                        <div>Weight: <strong>{fc.units.weight}</strong></div>
                      </div>
                      <div className="text-xs text-slate-500 mt-2.5 pt-2.5 border-t border-slate-200/80">
                        Terminology: <em>{fc.salaryTerminology.gross}</em> / <em>{fc.salaryTerminology.net}</em>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 5: LANGUAGES & BCP 47 LOCALES */}
        {/* ========================================================================= */}
        {activeTab === 'languages-locales' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">BCP 47 & Unicode CLDR Locales</h3>
              <p className="text-xs text-slate-500">Strict BCP 47 compliance with standard numbering systems and calendar rules.</p>
            </div>

            {/* Live Formatter Sandbox */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
              <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Live Locale Formatter Sandbox</h4>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Amount to Format</label>
                  <input
                    type="number"
                    value={formatterAmount}
                    onChange={(e) => setFormatterAmount(parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-bold bg-white text-slate-800"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Currency Code</label>
                  <select
                    value={formatterCurrency}
                    onChange={(e) => setFormatterCurrency(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-bold bg-white text-slate-800"
                  >
                    {['USD', 'EUR', 'GBP', 'CAD', 'XAF', 'SAR', 'NGN', 'JPY'].map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">BCP 47 Target Locale</label>
                  <select
                    value={formatterLocale}
                    onChange={(e) => setFormatterLocale(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-bold bg-white text-slate-800"
                  >
                    {LOCALES_REGISTRY.map((l) => (
                      <option key={l.bcp47} value={l.bcp47}>
                        {l.name} ({l.bcp47})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Result display */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4 border-t border-slate-200">
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="text-[10px] text-slate-500 uppercase font-bold">Standard Decimal</div>
                  <div className="text-lg font-black text-slate-900 mt-1 font-mono">
                    {YamooPayNumberFormatter.formatDecimal(formatterAmount, formatterLocale)}
                  </div>
                </div>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200">
                  <div className="text-[10px] text-blue-600 uppercase font-bold">Formatted Currency</div>
                  <div className="text-lg font-black text-blue-900 mt-1 font-mono">
                    {YamooPayCurrencyFormatter.formatCurrency(formatterAmount, formatterCurrency, formatterLocale, { disambiguateSymbol: true })}
                  </div>
                </div>
                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <div className="text-[10px] text-slate-500 uppercase font-bold">Date Medium</div>
                  <div className="text-lg font-black text-slate-900 mt-1 font-mono">
                    {YamooPayDateFormatter.formatDateOnly('2026-08-25', formatterLocale)}
                  </div>
                </div>
              </div>
            </div>

            {/* Locales List */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs">
              <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider mb-4">Supported BCP 47 Locales</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {LOCALES_REGISTRY.map((loc) => (
                  <div key={loc.bcp47} className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{loc.nativeName}</span>
                      <span className="font-mono text-[10px] font-bold px-2 py-0.5 bg-blue-100 text-blue-800 rounded">
                        {loc.bcp47}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 gap-1 text-slate-500 mt-2 text-[11px]">
                      <div>Decimal: <strong className="text-slate-800 font-mono">&apos;{loc.decimalSeparator}&apos;</strong></div>
                      <div>Group: <strong className="text-slate-800 font-mono">&apos;{loc.groupingSeparator}&apos;</strong></div>
                      <div>Date: <strong className="text-slate-800 font-mono">{loc.dateFormat}</strong></div>
                      <div>Dir: <strong className="text-slate-800">{loc.textDirection}</strong></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 6: TRANSLATION MANAGEMENT & MEMORY */}
        {/* ========================================================================= */}
        {activeTab === 'tms-translations' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <div>
                <h3 className="text-base font-bold text-slate-900">Translation Management Platform & Memory (TMS)</h3>
                <p className="text-xs text-slate-500">Semantic keys, translation memory reuse, and mandatory human review for financial & legal strings.</p>
              </div>
            </div>

            {/* Translation Memory Highlights */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
              <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Translation Memory (TM) Matches</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {TRANSLATION_MEMORY_STORE.map((tm) => (
                  <div key={tm.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-xs space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="font-mono font-bold text-blue-600">{tm.targetLocale}</span>
                      <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded">
                        {tm.qualityScore}% Match
                      </span>
                    </div>
                    <div className="text-slate-500">Source: &quot;{tm.sourceTextEn}&quot;</div>
                    <div className="font-bold text-slate-900">Target: &quot;{tm.translatedText}&quot;</div>
                    <div className="text-[10px] text-slate-400">Reused {tm.useCount} times in {tm.domain}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Messages and Units Table */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 font-bold text-slate-900 text-sm">
                Managed Message Keys & Translations
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3">Message Key</th>
                      <th className="px-6 py-3">Sensitivity</th>
                      <th className="px-6 py-3">Source Text (English)</th>
                      <th className="px-6 py-3">French (fr-FR)</th>
                      <th className="px-6 py-3">Arabic (ar-SA)</th>
                      <th className="px-6 py-3">Reviewer</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                    {SAMPLE_TRANSLATION_MESSAGES.map((msg) => {
                      const frUnit = SAMPLE_TRANSLATION_UNITS.find((u) => u.messageKey === msg.key && u.locale === 'fr-FR');
                      const arUnit = SAMPLE_TRANSLATION_UNITS.find((u) => u.messageKey === msg.key && u.locale === 'ar-SA');
                      return (
                        <tr key={msg.key} className="hover:bg-slate-50/80">
                          <td className="px-6 py-3 font-mono font-bold text-blue-600">{msg.key}</td>
                          <td className="px-6 py-3">
                            <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                              msg.sensitivity === 'LEGAL' || msg.sensitivity === 'FINANCIAL'
                                ? 'bg-amber-100 text-amber-800'
                                : 'bg-slate-100 text-slate-700'
                            }`}>
                              {msg.sensitivity}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-slate-900 font-medium">{msg.sourceTextEn}</td>
                          <td className="px-6 py-3">{frUnit ? frUnit.translatedText : '—'}</td>
                          <td className="px-6 py-3 text-right" dir="rtl">{arUnit ? arUnit.translatedText : '—'}</td>
                          <td className="px-6 py-3 text-slate-500">{frUnit?.reviewer || 'Automated'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 7: FINANCIAL TERMINOLOGY & GUARDRAILS */}
        {/* ========================================================================= */}
        {activeTab === 'terminology' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">Financial Terminology Governance & Do-Not-Translate Guardrails</h3>
              <p className="text-xs text-slate-500">Concept-first financial terms, strictly forbidden translations, and protected global brand entities.</p>
            </div>

            {/* Approved Financial Terms */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
              <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Concept-First Financial Terms</h4>
              <div className="space-y-4">
                {FINANCIAL_TERMINOLOGY_REGISTRY.map((term) => (
                  <div key={term.id} className="p-4 rounded-xl border border-slate-200 bg-slate-50 text-xs space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-blue-700 text-sm">{term.conceptKey}</span>
                        <span className="text-[10px] px-2 py-0.5 bg-blue-100 text-blue-800 font-bold rounded">
                          {term.domain}
                        </span>
                      </div>
                      <span className="text-[11px] text-slate-500">Source: <strong>{term.sourceTermEn}</strong></span>
                    </div>
                    <p className="text-slate-600 text-[11px]">{term.definition}</p>
                    
                    {/* Approved terms mapping */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-2 border-t border-slate-200">
                      {Object.entries(term.approvedTermsByLocale).map(([loc, t]) => (
                        <div key={loc} className="p-2 rounded bg-white border border-slate-200">
                          <span className="text-[10px] font-mono text-slate-400 font-bold block">{loc}</span>
                          <span className="font-bold text-slate-900">{t}</span>
                        </div>
                      ))}
                    </div>

                    {/* Forbidden translations */}
                    {Object.keys(term.forbiddenTerms).length > 0 && (
                      <div className="pt-2 flex items-start gap-2 text-[11px] text-rose-700 bg-rose-50 p-2.5 rounded-lg border border-rose-200">
                        <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                        <div>
                          <strong>Strictly Forbidden Translations:</strong>
                          <div className="mt-0.5">
                            {Object.entries(term.forbiddenTerms).map(([loc, terms]) => (
                              <span key={loc} className="mr-3">
                                <strong>{loc}</strong>: {terms.join(', ')}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Do Not Translate Registry */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs">
              <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider mb-3">Do-Not-Translate Registry</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                {DO_NOT_TRANSLATE_REGISTRY.map((dnt, idx) => (
                  <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 text-xs">
                    <div className="flex items-center justify-between">
                      <span className="font-bold text-slate-900">{dnt.term}</span>
                      <span className="text-[9px] px-1.5 py-0.5 bg-slate-200 font-mono rounded">{dnt.category}</span>
                    </div>
                    <p className="text-[11px] text-slate-500 mt-1">{dnt.reason}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 8: NUMBER PARSER SANDBOX */}
        {/* ========================================================================= */}
        {activeTab === 'input-parser' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">Localized Financial Input Parser Sandbox</h3>
              <p className="text-xs text-slate-500">
                Lossless parser capable of resolving commas, spaces, dots, apostrophes, and Arabic numerals without numerical distortion.
              </p>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Input tester */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
                <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Interactive Parser Test</h4>
                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Enter Raw User Input (Any Format)</label>
                  <input
                    type="text"
                    value={testInput}
                    onChange={(e) => setTestInput(e.target.value)}
                    placeholder="e.g. 1 250,75 or 1.250,75 or ١٬٢٥٠٫٧٥"
                    className="w-full px-4 py-2.5 rounded-xl border border-slate-300 text-sm font-bold bg-white text-slate-900"
                  />
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-700 block mb-1">Active User Context Locale</label>
                  <select
                    value={testLocale}
                    onChange={(e) => setTestLocale(e.target.value)}
                    className="w-full px-3 py-2 rounded-xl border border-slate-300 text-xs font-bold bg-white text-slate-800"
                  >
                    {LOCALES_REGISTRY.map((l) => (
                      <option key={l.bcp47} value={l.bcp47}>
                        {l.name} ({l.bcp47}) — Dec: &apos;{l.decimalSeparator}&apos;, Grp: &apos;{l.groupingSeparator}&apos;
                      </option>
                    ))}
                  </select>
                </div>

                {/* Preset quick test buttons */}
                <div className="pt-2">
                  <div className="text-xs font-bold text-slate-500 mb-2">Quick Presets:</div>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { val: '1,000,500.50', loc: 'en-US', label: 'US (1,000,500.50)' },
                      { val: '1 000 500,50', loc: 'fr-FR', label: 'French (1 000 500,50)' },
                      { val: '1.000.500,50', loc: 'de-DE', label: 'German (1.000.500,50)' },
                      { val: '1\'000\'500.50', loc: 'de-DE', label: 'Swiss (1\'000\'500.50)' },
                      { val: '١٬٢٥٠٫٧٥', loc: 'ar-SA', label: 'Arabic Digits (١٬٢٥٠٫٧٥)' }
                    ].map((p, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          setTestInput(p.val);
                          setTestLocale(p.loc);
                        }}
                        className="px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-medium text-slate-700 transition-colors"
                      >
                        {p.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Extraction result */}
              <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
                <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Lossless Extraction Result</h4>
                <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 space-y-2">
                  <div className="text-[10px] font-bold text-blue-600 uppercase">Exact Float Value</div>
                  <div className="text-2xl font-black text-blue-950 font-mono">
                    {parsedResult.value}
                  </div>
                  <div className="text-xs text-blue-800">
                    Valid Decimal: <strong>{parsedResult.isValid ? 'YES' : 'NO'}</strong> • Canonical String: <code className="font-mono bg-blue-100 px-1 py-0.5 rounded">{parsedResult.normalizedString}</code>
                  </div>
                </div>

                <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 space-y-1.5 text-xs text-slate-600">
                  <div className="font-bold text-slate-900">Safety Verification:</div>
                  <p>1. Localized formatted representations are strictly isolated to UI display layers.</p>
                  <p>2. Financial calculation cores operate exclusively on lossless canonical decimals.</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 9: RTL & BIDIRECTIONAL UI STUDIO */}
        {/* ========================================================================= */}
        {activeTab === 'rtl-studio' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">RTL & Bidirectional UI Engineering</h3>
              <p className="text-xs text-slate-500">Logical CSS start/end semantics, selective icon mirroring, and mixed-direction bidi isolation.</p>
            </div>

            {/* Arabic Live Layout Component Preview */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
              <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Arabic (RTL) Live UI Simulation</h4>
              <div className="p-6 bg-slate-50 rounded-2xl border border-slate-200" dir="rtl">
                <div className="flex items-center justify-between pb-4 border-b border-slate-200">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-600 text-white rounded-xl flex items-center justify-center font-black">
                      YP
                    </div>
                    <div>
                      <div className="font-black text-slate-900 text-base">يامو باي — حاسبة الرواتب والضرائب</div>
                      <div className="text-xs text-slate-500">المملكة العربية السعودية • نظام التأمينات (GOSI)</div>
                    </div>
                  </div>
                  <button className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-xl flex items-center gap-1.5">
                    <span>احسب الراتب</span>
                    <ChevronRight className="w-4 h-4 rotate-180" />
                  </button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-4">
                  <div className="p-3.5 bg-white rounded-xl border border-slate-200 text-right">
                    <div className="text-xs text-slate-500">الراتب الإجمالي</div>
                    <div className="text-lg font-black text-slate-900 mt-1 font-mono">١٥٬٠٠٠٫٠٠ ر.س</div>
                  </div>
                  <div className="p-3.5 bg-white rounded-xl border border-slate-200 text-right">
                    <div className="text-xs text-slate-500">استقطاع التأمينات (9.75%)</div>
                    <div className="text-lg font-black text-rose-600 mt-1 font-mono">١٬٤٦٢٫٥٠ ر.س</div>
                  </div>
                  <div className="p-3.5 bg-white rounded-xl border border-slate-200 text-right">
                    <div className="text-xs text-slate-500">صافي الراتب الشهري</div>
                    <div className="text-lg font-black text-emerald-600 mt-1 font-mono">١٣٬٥٣٧٫٥٠ ر.س</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Pseudolocalization Tool */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
              <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Pseudolocalization & Overflow Stress Test</h4>
              <div>
                <label className="text-xs font-bold text-slate-700 block mb-1">Source String</label>
                <input
                  type="text"
                  value={pseudoText}
                  onChange={(e) => setPseudoText(e.target.value)}
                  className="w-full px-4 py-2 rounded-xl border border-slate-300 text-xs font-bold bg-white text-slate-800"
                />
              </div>
              <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
                <div className="text-[10px] font-bold text-amber-700 uppercase">Accented & Expanded (+35%) Pseudoloc Preview</div>
                <div className="font-mono text-xs text-amber-950 mt-1 font-bold">
                  {PseudolocalizationEngine.transform(pseudoText, 35)}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 10: HREFLANG & INTERNATIONAL SEO */}
        {/* ========================================================================= */}
        {activeTab === 'international-seo' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">International SEO & Hreflang Graph Service</h3>
              <p className="text-xs text-slate-500">Distinct localized URLs, reciprocal alternate links, and localized search intent mapping.</p>
            </div>

            {/* Live Hreflang Generator */}
            <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-xs space-y-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h4 className="font-black text-sm text-slate-900 uppercase tracking-wider">Hreflang Graph Generator</h4>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500 font-bold">Route Path:</span>
                  <input
                    type="text"
                    value={hreflangPath}
                    onChange={(e) => setHreflangPath(e.target.value)}
                    className="px-3 py-1.5 rounded-xl border border-slate-300 text-xs font-mono font-bold bg-white text-slate-800"
                  />
                </div>
              </div>

              {/* Status */}
              <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-xl border border-emerald-200 text-xs text-emerald-900 font-medium">
                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                <span>All {hreflangTags.length} generated alternate tags have verified reciprocal links and valid x-default.</span>
              </div>

              {/* Tags display */}
              <div className="p-4 bg-slate-900 text-slate-100 rounded-xl font-mono text-xs overflow-x-auto space-y-1.5">
                {hreflangTags.map((tag, idx) => (
                  <div key={idx} className="text-blue-300">
                    &lt;link rel=&quot;alternate&quot; hreflang=&quot;<span className="text-emerald-400">{tag.locale}</span>&quot; href=&quot;<span className="text-amber-300">{tag.url}</span>&quot; /&gt;
                  </div>
                ))}
              </div>
            </div>

            {/* Local Search Intent Matrix */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 font-bold text-slate-900 text-sm">
                Localized Search Intent & Subdirectory URL Matrix
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-left text-xs">
                  <thead className="bg-slate-100 text-slate-600 font-bold uppercase tracking-wider border-b border-slate-200">
                    <tr>
                      <th className="px-6 py-3">Market</th>
                      <th className="px-6 py-3">Locale</th>
                      <th className="px-6 py-3">Target Keyword</th>
                      <th className="px-6 py-3">Localized Subdirectory Slug</th>
                      <th className="px-6 py-3">Volume Tier</th>
                      <th className="px-6 py-3">Indexable</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200 font-medium text-slate-700">
                    {LOCALIZED_SEARCH_INTENTS.map((intent, idx) => (
                      <tr key={idx} className="hover:bg-slate-50/80">
                        <td className="px-6 py-3 font-bold text-slate-900">{intent.marketCode}</td>
                        <td className="px-6 py-3 font-mono text-blue-600">{intent.locale}</td>
                        <td className="px-6 py-3 font-bold text-slate-800">{intent.targetKeyword}</td>
                        <td className="px-6 py-3 font-mono text-slate-600">/{intent.locale.toLowerCase()}/{intent.localizedSlug}</td>
                        <td className="px-6 py-3 font-bold text-emerald-600">{intent.searchVolumeTier}</td>
                        <td className="px-6 py-3">
                          <span className="px-2 py-0.5 rounded bg-emerald-100 text-emerald-800 font-bold text-[10px]">
                            INDEXABLE
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* TAB 11: INCIDENTS & KILL SWITCHES */}
        {/* ========================================================================= */}
        {activeTab === 'incidents' && (
          <div className="space-y-6">
            <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs">
              <h3 className="text-base font-bold text-slate-900">Market Incident Governance & Isolation Kill Switches</h3>
              <p className="text-xs text-slate-500">Real-time isolation of localized defects without taking down the global platform.</p>
            </div>

            {/* Incidents Table */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
              <div className="px-6 py-4 border-b border-slate-200 bg-slate-50 font-bold text-slate-900 text-sm">
                Active & Resolved Localization Incidents
              </div>
              <div className="divide-y divide-slate-200">
                {SAMPLE_LOCALIZATION_INCIDENTS.map((inc) => (
                  <div key={inc.id} className="p-6 flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="space-y-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-bold text-xs text-blue-600">{inc.id}</span>
                        <span className="text-[10px] px-2 py-0.5 bg-slate-100 rounded font-mono text-slate-700">{inc.marketCode} ({inc.locale})</span>
                        <span className="text-[10px] px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded">{inc.status}</span>
                      </div>
                      <p className="text-xs text-slate-800 font-medium">{inc.description}</p>
                      <div className="text-[10px] text-slate-400">Reported: {inc.reportedAt}</div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button className="px-3.5 py-1.5 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200 text-xs font-bold rounded-xl transition-colors cursor-pointer">
                        Trigger Market Kill Switch
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
